import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { InvitationError, acceptInvitation } from '../../services/invitations';
import type { InvitationPreview } from '../../types/invitations';
import type { AcceptInviteOutcome } from './useInvitationPreview';
import { InviteHeader, SignInLink } from './AcceptInviteLayout';

/**
 * The half of the accept-invitation page that takes a password.
 *
 * Its own component so the form state lives and dies with the form: a submission that ends the page
 * unmounts this, which is what makes "accepted, and also showing a validation error" unrepresentable
 * rather than merely unreached.
 */

/** Mirrors the server's own rules so the common mistakes never cost a round trip. */
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 128;
const MAX_NAME_LENGTH = 100;

type AcceptInviteFormData = {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
};

interface AcceptInviteFormProps {
  token: string;
  preview: InvitationPreview;
  /** Called only when the result ends the page. A refusal the invitee can fix keeps the form up. */
  onOutcome: (outcome: AcceptInviteOutcome) => void;
}

export const AcceptInviteForm = ({ token, preview, onOutcome }: AcceptInviteFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const emailHintId = useId();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptInviteFormData>({ mode: 'onSubmit' });

  const password = watch('password');

  const onSubmit = async (data: AcceptInviteFormData) => {
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const accepted = await acceptInvitation(token, {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        password: data.password,
      });
      onOutcome({ phase: 'accepted', accepted });
    } catch (error: unknown) {
      if (!(error instanceof InvitationError)) {
        setSubmitError('Something went wrong. Your invitation link still works, so please try again.');
        return;
      }

      switch (error.kind) {
        // The link died, or the program was paused, between loading this page and submitting it.
        // Either way the form is no longer the right thing to show.
        case 'invalid':
          onOutcome({ phase: 'invalid' });
          break;
        case 'paused':
          onOutcome({ phase: 'paused', message: error.message });
          break;
        case 'already_registered':
          onOutcome({ phase: 'registered', message: error.message, email: preview.email });
          break;
        // 400, 429 and 500 all leave the invitation usable, so the form stays put and says why.
        default:
          setSubmitError(error.message);
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <InviteHeader
        title={`Join ${preview.organizationName}`}
        subtitle="Set your password and your account is ready."
      />

      <form className="accept-invite-form" onSubmit={handleSubmit(onSubmit)}>
        {submitError && (
          <div className="accept-invite-message accept-invite-message--error" role="alert">
            <p>{submitError}</p>
          </div>
        )}

        <div className="accept-invite-inputs">
          <div>
            {/*
              Read-only rather than editable: the account is created for the invited address whatever
              this form says, so an editable field would be a lie.
            */}
            <TextInput
              label="Email"
              type="email"
              value={preview.email}
              readOnly
              aria-describedby={emailHintId}
              data-testid="invited-email"
            />
            <p className="accept-invite-hint" id={emailHintId}>
              Your account is created for this address.
            </p>
          </div>

          <TextInput
            label="First Name"
            autoComplete="given-name"
            error={errors.firstName?.message}
            disabled={isSubmitting}
            data-testid="first-name-input"
            {...register('firstName', {
              required: 'Enter your first name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `First name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Last Name"
            autoComplete="family-name"
            error={errors.lastName?.message}
            disabled={isSubmitting}
            data-testid="last-name-input"
            {...register('lastName', {
              required: 'Enter your last name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `Last name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Password"
            type="password"
            placeholder="******"
            autoComplete="new-password"
            error={errors.password?.message}
            disabled={isSubmitting}
            data-testid="password-input"
            {...register('password', {
              required: 'Choose a password',
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
              },
              maxLength: {
                value: MAX_PASSWORD_LENGTH,
                message: `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Confirm Password"
            type="password"
            placeholder="******"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            disabled={isSubmitting}
            data-testid="confirm-password-input"
            {...register('confirmPassword', {
              required: 'Confirm your password',
              validate: (value) => value === password || 'Passwords do not match',
            })}
          />
        </div>

        <div className="accept-invite-actions">
          <Button type="submit" fullWidth loading={isSubmitting} data-testid="create-account-button">
            Create Account
          </Button>

          <SignInLink>Already have an account? Sign in</SignInLink>
        </div>
      </form>
    </>
  );
};
